'use client';

import { useState } from 'react';
import { X, Calendar, Phone, Clock, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { analytics } from '@/utils/analytics';

interface TimeSlot {
  date: string;
  time: string;
}

interface AppointmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  gemeindeName?: string;
}

export function AppointmentForm({ isOpen, onClose, gemeindeName }: AppointmentFormProps) {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ date: '', time: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Generate dates for the next 10 days
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('de-DE', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
          })
        });
      }
    }
    
    return dates;
  };

  const availableDates = getAvailableDates();

  const timeOptions = [
    { value: '09:00', label: '09:00 - 10:00' },
    { value: '10:00', label: '10:00 - 11:00' },
    { value: '11:00', label: '11:00 - 12:00' },
    { value: '14:00', label: '14:00 - 15:00' },
    { value: '15:00', label: '15:00 - 16:00' },
    { value: '16:00', label: '16:00 - 17:00' },
  ];

  const handleAddSlot = () => {
    if (timeSlots.length < 3) {
      setTimeSlots([...timeSlots, { date: '', time: '' }]);
    }
  };

  const handleRemoveSlot = (index: number) => {
    if (timeSlots.length > 1) {
      setTimeSlots(timeSlots.filter((_, i) => i !== index));
    }
  };

  const handleSlotChange = (index: number, field: 'date' | 'time', value: string) => {
    const newSlots = [...timeSlots];
    newSlots[index][field] = value;
    setTimeSlots(newSlots);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && name.trim() && phoneNumber.trim()) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const canProceed = currentStep === 1 
    ? name.trim() && phoneNumber.trim()
    : timeSlots.some(slot => slot.date && slot.time);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    analytics.trackFormInteraction('appointment_form', 'submit', { 
      municipality: gemeindeName 
    });
    
    // Validate name
    if (!name.trim()) {
      alert('Bitte geben Sie Ihren Namen ein.');
      return;
    }
    
    // Validate phone number
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phoneNumber) || phoneNumber.length < 10) {
      alert('Bitte geben Sie eine gültige Telefonnummer ein.');
      return;
    }

    // Validate at least one complete time slot
    const validSlots = timeSlots.filter(slot => slot.date && slot.time);
    if (validSlots.length === 0) {
      alert('Bitte wählen Sie mindestens einen Terminvorschlag aus.');
      return;
    }

    setIsSubmitting(true);

    // Prepare email data
    const appointmentData = {
      name: name.trim(),
      phoneNumber,
      gemeinde: gemeindeName || 'Nicht angegeben',
      timeSlots: validSlots.map(slot => ({
        date: new Date(slot.date).toLocaleDateString('de-DE', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        time: slot.time
      })),
      submittedAt: new Date().toISOString()
    };

    try {
      // Send email via API route
      const response = await fetch('/api/appointment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      setIsSuccess(true);
      analytics.trackAppointmentBooking(gemeindeName || '', true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setName('');
        setPhoneNumber('');
        setTimeSlots([{ date: '', time: '' }]);
        setIsSuccess(false);
        setCurrentStep(1);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      analytics.trackAppointmentBooking(gemeindeName || '', false);
      analytics.trackError(error as Error, 'appointment_booking');
      alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="bg-white border-t-2 border-green-600 rounded-b-lg shadow-lg p-4 sm:p-6 mt-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Beratungstermin vereinbaren
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 transition-colors p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress indicator - Mobile only */}
            <div className="flex md:hidden items-center justify-center mb-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${currentStep >= 1 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                1
              </div>
              <div className={`w-16 h-1 ${currentStep >= 2 ? 'bg-green-600' : 'bg-gray-300'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${currentStep >= 2 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                2
              </div>
            </div>

            {/* Success State */}
            {isSuccess ? (
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="py-8 text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Vielen Dank für Ihre Anfrage!
                </h4>
                <p className="text-gray-600">
                  Wir werden Sie innerhalb der nächsten 24 Stunden kontaktieren.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit}>
                {gemeindeName && (
                  <p className="text-sm text-gray-600 mb-4">
                    Energiegemeinschaft {gemeindeName}
                  </p>
                )}

                {/* Desktop: All fields visible, Mobile: Step-based */}
                <div className="md:block">
                  {/* Step 1 Content - Always visible on desktop */}
                  <div className={`${currentStep === 1 ? 'block' : 'hidden md:block'}`}>
                    {/* Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ihr Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Max Mustermann"
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                        required
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="mb-4 md:mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="inline h-4 w-4 mr-1" />
                        Ihre Telefonnummer
                      </label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+43 123 456789"
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                        required
                      />
                    </div>

                    {/* Mobile: Next button */}
                    <div className="md:hidden">
                      <button
                        type="button"
                        onClick={handleNextStep}
                        disabled={!canProceed}
                        className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        Weiter zu Terminen
                        <ChevronRight className="h-5 w-5 ml-2" />
                      </button>
                    </div>
                  </div>

                  {/* Step 2 Content - Always visible on desktop */}
                  <div className={`${currentStep === 2 ? 'block' : 'hidden md:block'}`}>
                    {/* Time Slots */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        Ihre Terminvorschläge
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Wählen Sie bis zu 3 Zeitfenster aus, an denen Sie erreichbar sind.
                      </p>

                      {timeSlots.map((slot, index) => (
                        <div key={index} className="mb-3">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <select
                              value={slot.date}
                              onChange={(e) => handleSlotChange(index, 'date', e.target.value)}
                              className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                            >
                              <option value="">Datum wählen</option>
                              {availableDates.map(date => (
                                <option key={date.value} value={date.value}>
                                  {date.label}
                                </option>
                              ))}
                            </select>

                            <select
                              value={slot.time}
                              onChange={(e) => handleSlotChange(index, 'time', e.target.value)}
                              className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                            >
                              <option value="">Uhrzeit wählen</option>
                              {timeOptions.map(time => (
                                <option key={time.value} value={time.value}>
                                  {time.label}
                                </option>
                              ))}
                            </select>

                            {timeSlots.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveSlot(index)}
                                className="text-red-500 hover:text-red-700 px-2 self-center"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {timeSlots.length < 3 && (
                        <button
                          type="button"
                          onClick={handleAddSlot}
                          className="text-sm text-green-600 hover:text-green-700 font-semibold"
                        >
                          + Weiteren Terminvorschlag hinzufügen
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3">
                  {/* Mobile: Back button on step 2 */}
                  {currentStep === 2 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="md:hidden flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
                    >
                      <ChevronLeft className="h-5 w-5 mr-2" />
                      Zurück
                    </button>
                  )}
                  
                  {/* Desktop: Cancel button */}
                  <button
                    type="button"
                    onClick={onClose}
                    className="hidden md:block flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Abbrechen
                  </button>
                  
                  {/* Submit button - always visible on desktop, only on step 2 for mobile */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !timeSlots.some(slot => slot.date && slot.time)}
                    className={`${currentStep === 1 ? 'hidden md:block' : ''} flex-1 px-4 py-2 sm:py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <Clock className="animate-spin h-4 w-4 mr-2" />
                        Wird gesendet...
                      </span>
                    ) : (
                      'Termin anfragen'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}